/**
 * Lightweight WebAudio sound effects — synthesised on the fly so we don't
 * have to bundle audio assets. Gated by the user's `sound` setting.
 *
 * Sounds:
 *  - `play`    — soft thump for placing a card on the table
 *  - `defend`  — quick double click for defending an attack
 *  - `error`   — short low buzz for rejected moves
 *  - `chime`   — bright two-note arpeggio for winning a round
 */

export type SfxKind = 'play' | 'defend' | 'error' | 'chime';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  // Mobile browsers suspend the context until user interaction; resume() is a no-op once it's running.
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

interface Tone {
  freq: number;
  /** Start time offset in seconds. */
  at: number;
  /** Duration in seconds. */
  dur: number;
  /** Peak gain (0..1). */
  gain: number;
  /** Oscillator type. */
  type?: OscillatorType;
}

const PRESETS: Record<SfxKind, Tone[]> = {
  play: [
    { freq: 220, at: 0,    dur: 0.07, gain: 0.18, type: 'triangle' },
    { freq: 140, at: 0.02, dur: 0.10, gain: 0.10, type: 'sine' },
  ],
  defend: [
    { freq: 380, at: 0,    dur: 0.05, gain: 0.20, type: 'triangle' },
    { freq: 520, at: 0.07, dur: 0.06, gain: 0.18, type: 'triangle' },
  ],
  error: [
    { freq: 110, at: 0,    dur: 0.16, gain: 0.20, type: 'sawtooth' },
    { freq: 95,  at: 0.05, dur: 0.16, gain: 0.16, type: 'sawtooth' },
  ],
  chime: [
    { freq: 660, at: 0,    dur: 0.12, gain: 0.18, type: 'triangle' },
    { freq: 880, at: 0.10, dur: 0.16, gain: 0.18, type: 'triangle' },
    { freq: 1320, at: 0.22, dur: 0.20, gain: 0.14, type: 'triangle' },
  ],
};

/** Fire an SFX. Silently no-ops when `enabled` is false or WebAudio isn't available. */
export function playSfx(kind: SfxKind, enabled = true) {
  if (!enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  for (const tone of PRESETS[kind]) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = tone.type || 'triangle';
    osc.frequency.value = tone.freq;
    osc.connect(gain);
    gain.connect(c.destination);
    // Envelope: quick attack, exponential release.
    const start = t0 + tone.at;
    const peak = tone.gain;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.dur);
    osc.start(start);
    osc.stop(start + tone.dur + 0.02);
  }
}
